import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { ContactForm } from "@/components/marketing/contact/ContactForm";

export const Route = createFileRoute("/about")({
  component: AboutPage,
});

const VALUES = [
  {
    title: "Privacy-first",
    copy: "No customer data leaves the environment you choose to host in. Our AI processes statistical embeddings, never raw documents or data rows - complete data privacy by design.",
  },
  {
    title: "Usage-based pricing",
    copy: "Pay for data, not per user. No adoption barriers, no per-seat decisions. Invite your whole team without budget anxiety.",
  },
  {
    title: "Practical",
    copy: "We deliver pragmatic solutions that teams can use immediately. Simple onboarding, secure pipelines, and tools that replace manual work from day one.",
  },
  {
    title: "Collaborative",
    copy: "We partner with teams to understand workflows and reduce friction. Self-service where possible, consultant-assisted where needed.",
  },
  {
    title: "Committed to nonprofits",
    copy: "We are passionate about empowering NGOs and nonprofit organisations. Special pricing is available so nonprofits can access the same enterprise-grade tools.",
  },
  {
    title: "Reliable",
    copy: "Engineering and support you can trust. Enterprise-grade security with authentication, role-based access, and multi-tenancy built in.",
  },
];

function AboutPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>About Larasoft</title>
        <meta
          name="description"
          content="Larasoft builds AI-powered knowledge bases, smart tables, and data connectors that help organisations capture data, connect systems, and unlock insights. Clients in 10+ countries."
        />
        <link rel="canonical" href="https://larasoft.global/about" />
        <meta property="og:title" content="About Larasoft" />
        <meta
          property="og:description"
          content="Larasoft builds AI-powered knowledge bases, smart tables, and data connectors that help organisations capture data, connect systems, and unlock insights."
        />
        <meta property="og:url" content="https://larasoft.global/about" />
      </Helmet>

      <Section
        containerClassName="grid grid-cols-1 items-center gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
        className="py-20"
      >
        <div className="space-y-6">
          <Badge className="w-max" variant="info">
            Who we are
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-brand-blue-dark">
            Unlocking the power of AI, automation, and institutional knowledge
          </h1>
          <p className="text-slate-600 max-w-xl">
            <span className="font-semibold text-brand-blue-dark">
              We are a passionate team
            </span>{" "}
            dedicated to helping{" "}
            <span className="font-semibold text-brand-blue-dark">
              people and organisations
            </span>{" "}
            unlock the value of AI, automation, and institutional knowledge.
            Larasoft builds AI-powered knowledge bases, smart tables, and data
            connectors - a unified platform that turns scattered information into
            actionable insights and gives leadership a single source of truth. We
            serve{" "}
            <span className="font-semibold text-brand-blue-dark">
              clients in over 10 countries
            </span>{" "}
            with usage-based pricing that removes adoption barriers.
          </p>
        </div>

        <div id="about-contact-form" className="relative">
          <ContactForm className="bg-white/85 backdrop-blur" source="marketing:about" />
        </div>
      </Section>

      <Section containerClassName="space-y-8" className="py-16">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-brand-blue-dark">Our values</h2>
          <p className="text-sm text-slate-600">
            Principles we bring to every engagement.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {VALUES.map((value) => (
            <Card key={value.title}>
              <CardContent className="space-y-3">
                <CardTitle>{value.title}</CardTitle>
                <CardDescription>{value.copy}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>
    </MarketingLayout>
  );
}
